import { Button, Checkbox, Tooltip, Input } from "@chakra-ui/react";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { uploadCertData } from "../../../../slice/certificateIssuerHomeSlice";
import "./CsvDataDisplay.css";

const ALLOWED_PACKAGINGS = ["pallet", "box", "carton", "package", "capsule", "bottle", "pack"];

function CsvDataDisplay(props) {
  const dispatch = useDispatch();
  const { csvData, closeUpload, eventId } = props;

  let column = (csvData?.[0] || []).map((c) => c?.trim()).filter((c) => c && c.length > 0);

  let rows = csvData?.slice(1);

  const normalize = (str) => str?.trim().toLowerCase();

  const normalizedCols = column.map(normalize);

  const mandatoryFieldLabels = [
    "Batch Number",
    "Manufacturing Date",
    "Expiry Date",
    "Country",
    "State",
    "City",
  ];

  const mandatoryFields = mandatoryFieldLabels.map((label) => normalize(label));

  const mandatoryColumns = column.filter((c) => mandatoryFields.includes(normalize(c)));

  const optionalColumns = column.filter((c) => !mandatoryFields.includes(normalize(c)));

  const [tableData, setTableData] = useState(rows);

  const [visibility, setVisibility] = useState(
    column.reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {}),
  );

  const handleVisibilityToggle = (field) => {
    setVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const dataEmpty = () => tableData.length === 0;

  const getMissingFields = () => {
    const missing = [];
    mandatoryFields.forEach((field, idx) => {
      if (!normalizedCols.includes(field)) {
        missing.push(mandatoryFieldLabels[idx]);
      }
    });
    return missing;
  };

  const missingField = () => getMissingFields().length > 0;

  const missingCell = () => {
    const indexes = mandatoryFields
      .map((field) => normalizedCols.indexOf(field))
      .filter((i) => i !== -1);

    for (const r of tableData) {
      for (const idx of indexes) {
        if (!r[idx] || r[idx].trim().length === 0) return true;
      }
    }
    return false;
  };

  const batchNumberIdx = normalizedCols.indexOf("batch number");

  const batchNumberContainsDuplicate = (idx) => {
    if (idx !== -1) {
      const set = new Set(tableData.map((r) => r[idx]));
      return set.size !== tableData.length;
    }
    return false;
  };

  const normalizeKey = (key) => {
    const lowerKey = key?.trim().toLowerCase() || "";

    let bestPackaging = null;
    let minIndex = Infinity;

    // Sort by length descending to match longer words first (e.g., 'package' before 'pack' if they start at the same index)
    const sortedPackagings = [...ALLOWED_PACKAGINGS].sort((a, b) => b.length - a.length);

    for (const packaging of sortedPackagings) {
      const idx = lowerKey.indexOf(packaging);
      if (idx !== -1 && idx < minIndex) {
        minIndex = idx;
        bestPackaging = packaging;
      }
    }

    if (bestPackaging) {
      return bestPackaging;
    }

    return lowerKey.replace(/\s+/g, "_");
  };

  const getSemanticRole = (normalizedKey) => {
    const roleMap = {
      batch_number: "batchNumber",
      manufacturing_date: "manufacturingDate",
      mfg_date: "manufacturingDate",
      expiry_date: "expiryDate",
      city: "city",
      state: "state",
      country: "country",
    };

    if (roleMap[normalizedKey]) {
      return roleMap[normalizedKey];
    }

    if (ALLOWED_PACKAGINGS.some((packaging) => normalizedKey.includes(packaging))) {
      return "hierarchyQuantity";
    }

    return null;
  };

  const getUnsupportedPackagings = () => {
    return column.filter((c) => {
      const lower = c.trim().toLowerCase();
      const isQuantityFormat = lower.includes("number of") || lower.includes(" per ");
      if (isQuantityFormat) {
        const parts = lower.split(" per ");

        const childKey = normalizeKey(parts[0]);
        if (!ALLOWED_PACKAGINGS.includes(childKey)) return true;

        if (parts.length > 1) {
          const parentKey = normalizeKey(parts[1]);
          if (!ALLOWED_PACKAGINGS.includes(parentKey)) return true;
        }
      }
      return false;
    });
  };

  const getHierarchyErrors = () => {
    const definedPackagings = new Set();
    const errors = [];

    column.forEach((col) => {
      const lower = col.trim().toLowerCase();
      if (!lower.includes("number of") && !lower.includes(" per ")) return;

      const parts = lower.split(" per ");
      const child = normalizeKey(parts[0]);
      if (!ALLOWED_PACKAGINGS.includes(child)) return; // Skipped if not valid, handled by unsupported checker

      let parent = null;
      if (parts.length > 1) {
        parent = normalizeKey(parts[1]);
        if (!ALLOWED_PACKAGINGS.includes(parent)) parent = null;
      }

      if (parent) {
        if (!definedPackagings.has(parent)) {
          errors.push(`"${col}" requires number of ${parent}s to be specified first`);
        }
      }

      definedPackagings.add(child);
    });

    return errors;
  };

  const unsupportedList = getUnsupportedPackagings();
  const hierarchyErrors = getHierarchyErrors();

  const isInvalid =
    dataEmpty() ||
    missingField() ||
    missingCell() ||
    batchNumberContainsDuplicate(batchNumberIdx) ||
    unsupportedList.length > 0 ||
    hierarchyErrors.length > 0;

  const renderTable = () => (
    <table className="csv-inline-table">
      <thead>
        <tr>
          {column.map((col, idx) => (
            <th key={idx}>{col}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {tableData.map((row, rowIdx) => (
          <tr key={rowIdx}>
            {column.map((col, colIdx) => (
              <td key={colIdx}>
                <Input
                  size="sm"
                  value={row[colIdx] || ""}
                  onChange={(e) => {
                    const newData = [...tableData];
                    newData[rowIdx][colIdx] = e.target.value;
                    setTableData(newData);
                  }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  const buildColumnsPayload = () => {
    return column.map((header, index) => {
      const key = normalizeKey(header);
      const role = getSemanticRole(key);

      return {
        key,
        header,
        visible: Boolean(visibility[header]),
        order: index,
        role,
      };
    });
  };

  const buildRowsPayload = (columnsPayload) => {
    return tableData.map((row) =>
      columnsPayload.map((col, index) => ({
        key: col.key,
        value: row[index] ?? "",
      })),
    );
  };

  const [isUploading, setIsUploading] = useState(false);
  const [disableCancelButton, setDisableCancelButton] = useState(false);

  const onUpload = async () => {
    setIsUploading(true);
    setDisableCancelButton(true);

    const columnsPayload = buildColumnsPayload();
    const rowsPayload = buildRowsPayload(columnsPayload);

    const res = (
      await dispatch(
        uploadCertData({
          eventId,
          columns: columnsPayload,
          rows: rowsPayload,
        }),
      )
    ).payload;

    if (res?.ok) {
      closeUpload(true);
    } else {
      setIsUploading(false);
      setDisableCancelButton(false);
    }
  };

  return (
    <div className="csv-display-wrapper">
      <div className="csv-fields-sidebar">
        <div className="mandatory-fields">
          <strong>Mandatory Fields</strong>
          {mandatoryColumns.map((field) => (
            <div key={field} className="field-label mandatory">
              <span>{field}</span>
              <Tooltip
                label={visibility[field] ? "Public (visible to all)" : "Private (restricted)"}
                hasArrow
              >
                <Checkbox
                  isChecked={visibility[field]}
                  onChange={() => handleVisibilityToggle(field)}
                  colorScheme="blue"
                >
                  {visibility[field] ? "Public" : "Private"}
                </Checkbox>
              </Tooltip>
            </div>
          ))}
        </div>

        <div className="optional-fields">
          <strong>Optional Fields</strong>
          {optionalColumns.map((field) => (
            <div key={field} className="field-label optional">
              <span>{field}</span>
              <Tooltip
                label={visibility[field] ? "Public (visible to all)" : "Private (restricted)"}
                hasArrow
              >
                <Checkbox
                  isChecked={visibility[field]}
                  onChange={() => handleVisibilityToggle(field)}
                  colorScheme="blue"
                >
                  {visibility[field] ? "Public" : "Private"}
                </Checkbox>
              </Tooltip>
            </div>
          ))}
        </div>
      </div>

      <div className="csv-display-main">
        <div className="csv-table-wrapper">{renderTable()}</div>

        {unsupportedList.length > 0 && (
          <div style={{ color: "red", textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
            Unsupported packaging variant detected: {unsupportedList.join(", ")}. Please rename to
            use standard variants (e.g. pallet, box, carton).
          </div>
        )}

        {hierarchyErrors.length > 0 && (
          <div style={{ color: "red", textAlign: "center", marginTop: "10px", fontWeight: "bold" }}>
            Hierarchy Definition Error: {hierarchyErrors.join(", ")}.
          </div>
        )}

        <div className="csv-table-button-container">
          <Button
            margin="10px 30px"
            backgroundColor="#007bff"
            color="white"
            _hover={{ bg: "#0069d9" }}
            onClick={onUpload}
            disabled={isInvalid || isUploading}
          >
            Upload
          </Button>

          <Button
            margin="10px 30px"
            backgroundColor="#007bff"
            color="white"
            _hover={{ bg: "#0069d9" }}
            onClick={closeUpload}
            disabled={disableCancelButton}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

CsvDataDisplay.propTypes = {
  csvData: PropTypes.array.isRequired,
  closeUpload: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
};

export default CsvDataDisplay;
